import { EProblemReportSortBy, EProblemReportSortOrder, EProblemReportStatus } from '@constants';
import { ProblemReportRow } from '@sdk/contracts';
import { Table } from 'antd';
import { TablePaginationConfig } from 'antd/es/table';
import { SorterResult } from 'antd/es/table/interface';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { areFiltersApply } from '../../utils/areFiltersApply';
import { EmptyProblemReportContent } from '../EmptyProblemReportContent';

import { getProblemReportsColumns } from './Columns';

import { useProblemReportsList } from '@/store';

type Props = {
  tabStatus?: EProblemReportStatus[];
  emptyContent: {
    title: string;
    description: string;
    emoji: string;
  };
};

export const ProblemReportsTable: React.FC<Props> = ({ tabStatus, emptyContent }) => {
  const { title, description, emoji } = emptyContent;
  const { t } = useTranslation(['DefaultTranslations', 'ProblemReportsTranslations']);
  const {
    isLoading,
    problemReports,
    setPageAndLimit,
    pagination,
    filter,
    search,
    sortBy,
    sortOrder,
    setSort,
    page,
    limit
  } = useProblemReportsList(tabStatus);
  const navigateTo = useNavigate();
  const { pathname } = useLocation();

  const filtersApplied = useMemo(() => areFiltersApply({ ...filter, search }), [filter, search]);

  const openDrawer = (problemReportId: string) =>
    navigateTo(`${pathname}?problemReportId=${problemReportId}`);

  const isAllTab = !tabStatus || tabStatus.length === 0;

  const handleTableChange = (
    paginationConfig: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<ProblemReportRow> | SorterResult<ProblemReportRow>[]
  ) => {
    const { current, pageSize } = paginationConfig;

    if (isAllTab && !Array.isArray(sorter)) {
      const { columnKey, order } = sorter;
      const newSortBy = columnKey as EProblemReportSortBy | undefined;
      const newSortOrder =
        order === 'ascend'
          ? EProblemReportSortOrder.ASC
          : order === 'descend'
          ? EProblemReportSortOrder.DESC
          : undefined;

      if (newSortBy !== sortBy || newSortOrder !== sortOrder) {
        setSort(newSortBy, newSortOrder);
        return;
      }
    }

    if (current !== undefined && pageSize !== undefined) {
      setPageAndLimit(current, pageSize);
    }
  };

  if (!filtersApplied && problemReports?.length === 0) {
    return <EmptyProblemReportContent title={title} description={description} emoji={emoji} />;
  }
  if (problemReports?.length === 0) {
    return (
      <EmptyProblemReportContent
        title={t('ProblemReportsTranslations:NO_PROBLEM_REPORTS_FILTERED')}
        description={t('ProblemReportsTranslations:NO_PROBLEM_REPORTS_FILTERED_DESCRIPTION')}
        emoji={'🔍'}
      />
    );
  }

  const columns = getProblemReportsColumns(
    t,
    tabStatus,
    isAllTab ? { sortBy, sortOrder, setSort } : undefined
  );

  return (
    <Table
      rowKey="_id"
      tableLayout="fixed"
      loading={isLoading}
      columns={columns}
      dataSource={problemReports}
      scroll={{ y: window.innerHeight - 350 }}
      onRow={({ _id }) => ({
        onClick: () => openDrawer(_id)
      })}
      onChange={handleTableChange}
      pagination={{
        current: page,
        pageSize: limit,
        total: pagination?.totalDocs ?? 0,
        showTotal: total => t('ProblemReportsTranslations:PROBLEM_REPORTS_TOTAL', { total })
      }}
    />
  );
};
